// src/pages/ResetPasswordComp.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import api from "../api/axios";
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { BrandLogo } from '../branding/BrandAsset';
import shapeIcon from "../assets/images/contact/4.png";

const ResetPasswordComp = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const isMobile = useMediaQuery('(max-width: 990px)');
  
  const [formData, setFormData] = useState({
    password: "",
    password_confirmation: ""
  });
  const [loading, setLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("info");
  const [resetSuccess, setResetSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

  useEffect(() => {
    document.title = "Reset Password | ProTradesInsight";
    
    if (!token) {
      showAlert("Invalid or missing reset token.", "error");
    }
  }, [token]);

  const handleCloseAlert = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenAlert(false);
  };

  const showAlert = (message, severity = "info") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setOpenAlert(true);
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));

    // Update password strength
    if (id === 'password') {
      const strength = calculatePasswordStrength(value);
      setPasswordStrength(strength);
    }
  };

  const calculatePasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;
    return score;
  };

  const validatePassword = (password) => {
    const requirements = {
      minLength: password.length >= 8,
      hasLetter: /[a-zA-Z]/.test(password),
      hasMixedCase: /[a-z]/.test(password) && /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSymbol: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    return requirements;
  };

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 0: return '#ff4444';
      case 1: return '#ffbb33';
      case 2: return '#ffdd59';
      case 3: return '#5cb85c';
      case 4: return '#00C851';
      default: return '#cccccc';
    }
  };

  const getPasswordStrengthLabel = () => {
    return ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'][passwordStrength] || '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate token
    if (!token) {
      showAlert("Invalid reset token. Please request a new password reset.", "error");
      setLoading(false);
      return;
    }

    // Validate password
    const passwordValidation = validatePassword(formData.password);
    const allRequirementsMet = Object.values(passwordValidation).every(Boolean);
    
    if (!allRequirementsMet) {
      showAlert("Password must contain: 8+ characters, uppercase, lowercase, number, and symbol", "error");
      setLoading(false);
      return;
    }
    
    if (formData.password !== formData.password_confirmation) {
      showAlert("Passwords do not match", "error");
      setLoading(false);
      return;
    }

    try {
      const response = await api.post('/auth/reset-password', {
        token,
        password: formData.password,
        password_confirmation: formData.password_confirmation
      });
      
      if (response.data.success) {
        setResetSuccess(true);
        showAlert(response.data.message || "Password reset successful! Redirecting to login...", "success");
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Password reset successful! Please login with your new password.',
              severity: 'success'
            } 
          });
        }, 3000);
      } else {
        showAlert(response.data.message || "Failed to reset password. Please try again.", "error");
      }
    } catch (error) {
      console.error('Reset password error:', error);
      const message = error.response?.data?.message || "Failed to reset password. Please try again.";
      showAlert(message, "error");
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (fieldId) => {
    const input = document.getElementById(fieldId);
    if (input) {
      input.type = input.type === "password" ? "text" : "password";
    }
  };

  if (!token) {
    return (
      <div className="reset-password-page">
        <Snackbar
          open={openAlert}
          autoHideDuration={6000}
          onClose={handleCloseAlert}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseAlert} severity="error" sx={{ width: '100%' }} variant="filled">
            {alertMessage}
          </Alert>
        </Snackbar>
        <div className="account padding-top padding-bottom sec-bg-color2" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
          <div className="container">
            <div className="account__wrapper">
              <div className="row g-4 justify-content-center">
                <div className="col-lg-6 col-md-8">
                  <div className="account__content account__content--style1 text-center">
                    <BrandLogo alt="ProTradesInsight" style={{ height: 50, maxWidth: '100%', objectFit: 'contain', marginBottom: 20 }} />
                    <h2>Invalid Reset Link</h2>
                    <p style={{ color: '#6a6a7a', marginBottom: 20 }}>
                      The password reset link is invalid or has expired.
                    </p>
                    <Link to="/forgot-password" className="trk-btn trk-btn--border trk-btn--primary d-block w-100 text-center" style={{ textDecoration: 'none' }}>
                      Request New Reset Link
                    </Link>
                    <Link to="/login" className="d-block mt-3" style={{ color: '#000080', textDecoration: 'none' }}>
                      ← Back to Login
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <Snackbar
        open={openAlert}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity={alertSeverity} sx={{ width: '100%' }} variant="filled">
          {alertMessage}
        </Alert>
      </Snackbar>

      <div className="account padding-top padding-bottom sec-bg-color2" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
        <div className="container">
          <div className="account__wrapper">
            <div className="row g-4 justify-content-center">
              <div className="col-lg-6 col-md-8">
                <div className="account__content account__content--style1">
                  {/* Logo */}
                  <div className="text-center mb-4">
                    <BrandLogo alt="ProTradesInsight" style={{ height: 50, maxWidth: '100%', objectFit: 'contain' }} />
                  </div>

                  <div className="account__header text-center">
                    <h2>Reset Password</h2>
                    <p>
                      {resetSuccess 
                        ? "Your password has been reset successfully!" 
                        : "Enter your new password below."
                      }
                    </p>
                  </div>

                  {resetSuccess ? (
                    <div className="text-center">
                      <Alert severity="success" sx={{ mb: 4, borderRadius: 2 }}>
                        <strong>Password Reset Successful!</strong>
                        <p className="mb-0 mt-1">Redirecting to login...</p>
                      </Alert>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="account__form">
                      <div className="row g-4">
                        <div className="col-12">
                          <div className="form-pass">
                            <label htmlFor="password" className="form-label">
                              New Password
                              <button
                                type="button"
                                className="btn btn-link p-0 ms-2"
                                onClick={() => setShowPasswordRequirements(!showPasswordRequirements)}
                                style={{ fontSize: '0.8rem' }}
                              >
                                {showPasswordRequirements ? 'Hide' : 'Show'} requirements
                              </button>
                            </label>
                            
                            {showPasswordRequirements && (
                              <div className="alert alert-info p-2 mb-2" style={{ fontSize: '0.8rem' }}>
                                <strong>Password must contain:</strong>
                                <ul className="mb-0">
                                  <li className={formData.password.length >= 8 ? 'text-success' : ''}>
                                    At least 8 characters
                                  </li>
                                  <li className={/[a-zA-Z]/.test(formData.password) ? 'text-success' : ''}>
                                    At least one letter
                                  </li>
                                  <li className={/[a-z]/.test(formData.password) && /[A-Z]/.test(formData.password) ? 'text-success' : ''}>
                                    Both uppercase and lowercase
                                  </li>
                                  <li className={/\d/.test(formData.password) ? 'text-success' : ''}>
                                    At least one number
                                  </li>
                                  <li className={/[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? 'text-success' : ''}>
                                    At least one symbol
                                  </li>
                                </ul>
                              </div>
                            )}
                            
                            <div className="input-group">
                              <input
                                className="form-control"
                                type="password"
                                id="password"
                                placeholder="Enter new password"
                                required
                                value={formData.password}
                                onChange={handleChange}
                              />
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => togglePasswordVisibility('password')}
                              >
                                <i className={`fa ${formData.password ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                              </button>
                            </div>
                            
                            {formData.password && (
                              <div className="mt-2">
                                <div className="progress" style={{ height: '5px' }}>
                                  <div
                                    className="progress-bar"
                                    role="progressbar"
                                    style={{
                                      width: `${(passwordStrength + 1) * 25}%`,
                                      backgroundColor: getPasswordStrengthColor()
                                    }}
                                  ></div>
                                </div>
                                <small className="text-muted">
                                  Password strength: {getPasswordStrengthLabel()}
                                </small>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="form-pass">
                            <label htmlFor="password_confirmation" className="form-label">Confirm Password</label>
                            <div className="input-group">
                              <input
                                className="form-control"
                                type="password"
                                id="password_confirmation"
                                placeholder="Confirm new password"
                                required
                                value={formData.password_confirmation}
                                onChange={handleChange}
                              />
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => togglePasswordVisibility('password_confirmation')}
                              >
                                <i className={`fa ${formData.password_confirmation ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <button
                        type="submit"
                        className="trk-btn trk-btn--border trk-btn--primary d-block mt-4 w-100"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Resetting...
                          </>
                        ) : 'Reset Password'}
                      </button>

                      <div className="text-center mt-3">
                        <Link to="/login" style={{ color: '#000080', textDecoration: 'none', fontWeight: 500 }}>
                          ← Back to Login
                        </Link>
                      </div>
                    </form>
                  )}

                  <div className="account__switch text-center mt-4">
                    <p style={{ color: '#666' }}>
                      Remember your password? <Link to="/login" style={{ color: '#000080', fontWeight: 600 }}>Login</Link>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="account__shape">
          <span className="account__shape-item account__shape-item--1">
            <img src={shapeIcon} alt="Shape icon" />
          </span>
        </div>
      </div>

      <style>
        {`
          .reset-password-page {
            background-color: #f8f9fa;
            min-height: 100vh;
          }
          .account__content {
            background: #ffffff;
            padding: 40px 35px;
            border-radius: 16px;
            box-shadow: 0 10px 40px rgba(0,0,0,0.06);
          }
          .account__header h2 {
            color: #1a1a2e;
            font-weight: 700;
            font-size: 28px;
            margin-bottom: 10px;
          }
          .account__header p {
            color: #6a6a7a;
            font-size: 15px;
            line-height: 1.6;
          }
          .account__form .form-label {
            font-weight: 500;
            color: #333;
            margin-bottom: 6px;
          }
          .account__form .form-control {
            background-color: #ffffff !important;
            color: #000000 !important;
            border: 1px solid #ced4da !important;
            padding: 12px 16px;
            border-radius: 8px;
            transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
          }
          .account__form .form-control:focus {
            border-color: #000080 !important;
            box-shadow: 0 0 0 0.2rem rgba(0, 0, 128, 0.25) !important;
          }
          .trk-btn {
            padding: 12px 24px;
            border-radius: 8px;
            font-weight: 600;
            font-size: 16px;
            transition: all 0.3s ease;
            border: 2px solid transparent;
            cursor: pointer;
          }
          .trk-btn--primary {
            background-color: #000000 !important;
            color: #ffffff !important;
            border-color: #000000 !important;
          }
          .trk-btn--primary:hover {
            background-color: #000080 !important;
            color: #ffffff !important;
            border-color: #000080 !important;
            transform: translateY(-2px);
            box-shadow: 0 4px 15px rgba(0,0,128,0.3);
          }
          .trk-btn:disabled {
            opacity: 0.7;
            cursor: not-allowed;
          }
          @media (max-width: 768px) {
            .account__content {
              padding: 30px 20px;
            }
          }
        `}
      </style>
    </div>
  );
};

export default ResetPasswordComp;
